import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '@chakra-ui/react'
import { asChakraVariable, themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { Radii, Size } from '~/types/ThemeTypes'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

import { sharedComponentColorKeys } from './_colors'

export enum MangoButtonVariant {
  Default_100 = 'default_100',
  Default_200 = 'default_200',
  Default_300 = 'default_300',
  Destructive_100 = 'destructive_100',
  Destructive_200 = 'destructive_200',
  Destructive_300 = 'destructive_300',
  Success_100 = 'success_100',
  Success_200 = 'success_200',
  Success_300 = 'success_300',
}

const Colors = sharedComponentColorKeys.MangoButton
const {
  Button: {
    FocusVisible: { BoxShadow: BoxShadowColor },
  },
} = themeColorSectionKeys.defaults

const MangoButtonParts = ['button', 'text', 'nodeContainer', 'icon'] as const

const helpers = createMultiStyleConfigHelpers(MangoButtonParts)
export const MangoButtonStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    button: {
      display: 'flex',
      gap: '0.5rem',
      flexShrink: 0,
      borderRadius: Radii._3xl,
      border: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      _disabled: {
        cursor: 'not-allowed',
        // The default disabled opacity is 0.4, but we control it with our background colors on buttons
        opacity: 1,
        userSelect: 'none',
      },
      _focusVisible: {
        boxShadow: `${asChakraVariable(BoxShadowColor)} 0px 0px 0px 2px`,
      },
    },
    nodeContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    icon: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    text: {
      textStyle: textStyleKeys.Body2,
    },
  },
  sizes: {
    [Size.md]: {
      button: {
        px: '1.25rem',
        height: '2.5rem',
      },
      icon: {
        boxSize: '1.25rem',
      },
    },
    [Size.sm]: {
      button: {
        px: '0.75rem',
        height: '1.75rem',
      },
      icon: {
        boxSize: '1rem',
      },
    },
  },
  variants: {
    [MangoButtonVariant.Default_300]: {
      button: {
        backgroundColor: Colors.Default_300.Background,
        _hover: {
          backgroundColor: Colors.Default_300.Hover.Background,
          boxShadow: `0 0.25rem 0.75rem 0 ${asChakraVariable(Colors.Default_300.Hover.BoxShadow)}`,
        },
        _active: {
          backgroundColor: Colors.Default_300.Active.Background,
          boxShadow: `0 0.25rem 0.75rem 0 ${asChakraVariable(Colors.Default_300.Active.BoxShadow)}`,
        },
        _disabled: {
          backgroundColor: Colors.Default_300.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Default_300.Disabled.Background,
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: Colors.Default_300.Disabled.Background,
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: Colors.Default_300.FontColor,
        _groupHover: {
          color: Colors.Default_300.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Default_300.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Default_300.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Default_300.Icon,
        _groupDisabled: {
          color: Colors.Default_300.Disabled.Icon,
        },
        _groupActive: {
          color: Colors.Default_300.Active.Icon,
        },
        _groupHover: {
          color: Colors.Default_300.Hover.Icon,
          _groupActive: {
            color: Colors.Default_300.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Default_300.Disabled.Icon,
          },
        },
      },
    },
    [MangoButtonVariant.Default_200]: {
      button: {
        backgroundColor: Colors.Default_200.Background,
        _hover: {
          backgroundColor: Colors.Default_200.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Default_200.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Default_200.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Default_200.Disabled.Background,
          },
          _active: {
            backgroundColor: Colors.Default_200.Disabled.Background,
          },
        },
      },
      text: {
        color: Colors.Default_200.FontColor,
        _groupHover: {
          color: Colors.Default_200.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Default_200.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Default_200.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Default_200.Icon,
        _groupActive: {
          color: Colors.Default_200.Active.Icon,
        },
        _groupHover: {
          color: Colors.Default_200.Hover.Icon,
        },
        _groupDisabled: {
          color: Colors.Default_200.Disabled.Icon,
          _groupActive: {
            color: Colors.Default_200.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Default_200.Disabled.Icon,
          },
        },
      },
    },
    [MangoButtonVariant.Default_100]: {
      button: {
        backgroundColor: Colors.Default_100.Background,
        _hover: {
          backgroundColor: Colors.Default_100.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Default_100.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Default_100.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Default_100.Disabled.Background,
          },
          _active: {
            backgroundColor: Colors.Default_100.Disabled.Background,
          },
        },
      },
      text: {
        color: Colors.Default_100.FontColor,
        _groupHover: {
          color: Colors.Default_100.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Default_100.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Default_100.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Default_100.Icon,
        _groupActive: {
          color: Colors.Default_100.Active.Icon,
        },
        _groupHover: {
          color: Colors.Default_100.Hover.Icon,
        },
        _groupDisabled: {
          color: Colors.Default_100.Disabled.Icon,
          _groupActive: {
            color: Colors.Default_100.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Default_100.Disabled.Icon,
          },
        },
      },
    },
    [MangoButtonVariant.Destructive_300]: {
      button: {
        backgroundColor: Colors.Destructive_300.Background,
        _hover: {
          backgroundColor: Colors.Destructive_300.Hover.Background,
          boxShadow: `0 0.25rem 0.75rem 0 ${asChakraVariable(Colors.Destructive_300.Hover.BoxShadow)}`,
        },
        _active: {
          backgroundColor: Colors.Destructive_300.Active.Background,
          boxShadow: `0 0.25rem 0.75rem 0 ${asChakraVariable(Colors.Destructive_300.Active.BoxShadow)}`,
        },
        _disabled: {
          backgroundColor: Colors.Destructive_300.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Destructive_300.Disabled.Background,
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: Colors.Destructive_300.Disabled.Background,
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: Colors.Destructive_300.FontColor,
        textStyle: textStyleKeys.Body2_Semibold,
        _groupHover: {
          color: Colors.Destructive_300.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Destructive_300.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Destructive_300.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Destructive_300.Icon,
        _groupActive: {
          color: Colors.Destructive_300.Active.Icon,
        },
        _groupHover: {
          color: Colors.Destructive_300.Hover.Icon,
        },
        _groupDisabled: {
          color: Colors.Destructive_300.Disabled.Icon,
          _groupActive: {
            color: Colors.Destructive_300.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Destructive_300.Disabled.Icon,
          },
        },
      },
    },
    [MangoButtonVariant.Destructive_200]: {
      button: {
        backgroundColor: Colors.Destructive_200.Background,
        _hover: {
          backgroundColor: Colors.Destructive_200.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Destructive_200.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Destructive_200.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Destructive_200.Disabled.Background,
          },
          _active: {
            backgroundColor: Colors.Destructive_200.Disabled.Background,
          },
        },
      },
      text: {
        color: Colors.Destructive_200.FontColor,
        _groupHover: {
          color: Colors.Destructive_200.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Destructive_200.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Destructive_200.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Destructive_200.Icon,
        _groupActive: {
          color: Colors.Destructive_200.Active.Icon,
        },
        _groupHover: {
          color: Colors.Destructive_200.Hover.Icon,
        },
        _groupDisabled: {
          color: Colors.Destructive_200.Disabled.Icon,
          _groupActive: {
            color: Colors.Destructive_200.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Destructive_200.Disabled.Icon,
          },
        },
      },
    },
    [MangoButtonVariant.Destructive_100]: {
      button: {
        backgroundColor: Colors.Destructive_100.Background,
        _hover: {
          backgroundColor: Colors.Destructive_100.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Destructive_100.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Destructive_100.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Destructive_100.Disabled.Background,
          },
          _active: {
            backgroundColor: Colors.Destructive_100.Disabled.Background,
          },
        },
      },
      text: {
        color: Colors.Destructive_100.FontColor,
        _groupHover: {
          color: Colors.Destructive_100.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Destructive_100.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Destructive_100.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Destructive_100.Icon,
        _groupActive: {
          color: Colors.Destructive_100.Active.Icon,
        },
        _groupHover: {
          color: Colors.Destructive_100.Hover.Icon,
        },
        _groupDisabled: {
          color: Colors.Destructive_100.Disabled.Icon,
          _groupActive: {
            color: Colors.Destructive_100.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Destructive_100.Disabled.Icon,
          },
        },
      },
    },
    [MangoButtonVariant.Success_300]: {
      button: {
        backgroundColor: Colors.Success_300.Background,
        _hover: {
          backgroundColor: Colors.Success_300.Hover.Background,
          boxShadow: `0 0.25rem 0.75rem 0 ${asChakraVariable(Colors.Success_300.Hover.BoxShadow)}`,
        },
        _active: {
          backgroundColor: Colors.Success_300.Active.Background,
          boxShadow: `0 0.25rem 0.75rem 0 ${asChakraVariable(Colors.Success_300.Active.BoxShadow)}`,
        },
        _disabled: {
          backgroundColor: Colors.Success_300.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Success_300.Disabled.Background,
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: Colors.Success_300.Disabled.Background,
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: Colors.Success_300.FontColor,
        _groupHover: {
          color: Colors.Success_300.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Success_300.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Success_300.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Success_300.Icon,
        _groupDisabled: {
          color: Colors.Success_300.Disabled.Icon,
        },
        _groupActive: {
          color: Colors.Success_300.Active.Icon,
        },
        _groupHover: {
          color: Colors.Success_300.Hover.Icon,
          _groupActive: {
            color: Colors.Success_300.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Success_300.Disabled.Icon,
          },
        },
      },
    },
    [MangoButtonVariant.Success_200]: {
      button: {
        backgroundColor: Colors.Success_200.Background,
        _hover: {
          backgroundColor: Colors.Success_200.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Success_200.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Success_200.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Success_200.Disabled.Background,
          },
          _active: {
            backgroundColor: Colors.Success_200.Disabled.Background,
          },
        },
      },
      text: {
        color: Colors.Success_200.FontColor,
        _groupHover: {
          color: Colors.Success_200.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Success_200.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Success_200.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Success_200.Icon,
        _groupActive: {
          color: Colors.Success_200.Active.Icon,
        },
        _groupHover: {
          color: Colors.Success_200.Hover.Icon,
        },
        _groupDisabled: {
          color: Colors.Success_200.Disabled.Icon,
          _groupActive: {
            color: Colors.Success_200.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Success_200.Disabled.Icon,
          },
        },
      },
    },
    [MangoButtonVariant.Success_100]: {
      button: {
        backgroundColor: Colors.Success_100.Background,
        _hover: {
          backgroundColor: Colors.Success_100.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Success_100.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Success_100.Disabled.Background,
          _hover: {
            backgroundColor: Colors.Success_100.Disabled.Background,
          },
          _active: {
            backgroundColor: Colors.Success_100.Disabled.Background,
          },
        },
      },
      text: {
        color: Colors.Success_100.FontColor,
        _groupHover: {
          color: Colors.Success_100.Hover.FontColor,
        },
        _groupActive: {
          color: Colors.Success_100.Active.FontColor,
        },
        _groupDisabled: {
          color: Colors.Success_100.Disabled.FontColor,
        },
      },
      icon: {
        color: Colors.Success_100.Icon,
        _groupActive: {
          color: Colors.Success_100.Active.Icon,
        },
        _groupHover: {
          color: Colors.Success_100.Hover.Icon,
        },
        _groupDisabled: {
          color: Colors.Success_100.Disabled.Icon,
          _groupActive: {
            color: Colors.Success_100.Disabled.Icon,
          },
          _groupHover: {
            color: Colors.Success_100.Disabled.Icon,
          },
        },
      },
    },
  },
  defaultProps: {
    variant: MangoButtonVariant.Default_300,
    size: Size.md,
  },
})

export const useMangoButtonStyleConfig = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig(
    getName({ MangoButtonStyle }),
    props,
  ) as MultiStyleType<typeof MangoButtonParts>
}
